import React from 'react';
import { connectMediaStore } from '../../../_hocs/withOwnReduxStore';
import { compose } from 'redux';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import Flex from '@wix/da-ds/pkg/Flex';
import DeviationGrid from '../../DeviationGrid';
import PopupInnerLayout from '../../../_partials/PopupInnerLayout';
import { PapiDeviation } from '@wix/da-papi-types';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import {
  moveDeviationToPosition,
  MoveDeviationToPositionPayload,
} from '../../../actions';
import withCurrentGallectionDeviations, {
  WithGallectionDeviationsProps,
} from '../../../_hocs/withCurrentGallectionDeviations';
import withSelectedDeviations, {
  WithSelectedDeviationsProps,
} from '../../../_hocs/withSelectedDeviations';
import withCurrentGallection, {
  WithCurrentGallectionProps,
} from '../../../_hocs/withCurrentGallection';
import withGallections, {
  WithGallectionsProps,
} from '../../../_hocs/withGallections';
import withCurrentSubfolder, {
  WithCurrentSubfolderProps,
} from '../../../_hocs/withCurrentSubfolder';
import withCurrentGallectionSubfolders, {
  WithCurrentGallectionSubfoldersProps,
} from '../../../_hocs/withCurrentGallectionSubfolders';
import {
  isSpecialFullyLockedFolder,
  canGallectionHaveSubfolders,
  getCanEditGallection,
} from '../../../helpers';
import AddDeviationsButton from '../../../_partials/AddDeviationsButton';
import AddGallectionButton from '../../../_partials/AddGallectionButton';
import ManageableDeviation from '../../../_partials/ManageableDeviation';
import ManageableSelectedItemsPanel from '../../../_partials/ManageableSelectedItemsPanel';
import { isPremiumGallection } from '../../../../utils/gallections';
import SubmitDeviationsButton, {
  Props as SubmitButtonProps,
} from '../../../_partials/SubmitDeviationsButton';
import { normalizePremiumFolderData } from '../../../../utils/normalizePremiumFolderData';
import ManageableSubfolderSlider from '../../../_partials/ManageableSubfolderSlider';
import { GallectionType } from '../../../../types/gallection';

import gallectionDeviationManagerStyles from './GallectionDeviationManager.scss';

export interface Props {
  sideBarContent?: React.ReactNode;
}

const enhance = compose(
  withCurrentGallection,
  withCurrentSubfolder,
  withCurrentGallectionSubfolders,
  withCurrentGallectionDeviations,
  withSelectedDeviations,
  withGallections,
  connectMediaStore(null, {
    moveDeviationToPosition,
  }),
  withTranslation()
);
export class GallectionDeviationManager extends React.Component<
  Props &
    WithCurrentGallectionProps &
    WithCurrentSubfolderProps &
    WithCurrentGallectionSubfoldersProps &
    WithGallectionDeviationsProps &
    WithSelectedDeviationsProps &
    WithGallectionsProps &
    WithTranslation & {
      moveDeviationToPosition(payload: MoveDeviationToPositionPayload): void;
    },
  {
    showRemoveConfirmationPopup: boolean;
    showMediaSelector: boolean;
  }
> {
  render() {
    const {
      currentGallection,
      currentSubfolderId,
      selectedCount,
      sideBarContent,
      subfolders,
    } = this.props;

    const showSubtitle =
      !isSpecialFullyLockedFolder(currentGallection!) &&
      getCanEditGallection(currentGallection) &&
      selectedCount < 1 &&
      subfolders.length < 1;

    return (
      <>
        <PopupInnerLayout
          headerSubtitle={showSubtitle ? this.getSubtitle() : undefined}
          headerExtrasRightZone={this.renderRightZoneButtons()}
          headerExtrasBottomZone={
            <>
              {!currentSubfolderId && <ManageableSubfolderSlider />}
              <ManageableSelectedItemsPanel />
            </>
          }
          scrollableContent={this.renderContent()}
          sideBarContent={sideBarContent}
          onScrolledToBottom={this.fetchMoreGallectionDeviationsIfNeeded}
          onNotScrollable={this.fetchMoreGallectionDeviationsIfNeeded}
        />
      </>
    );
  }

  getSubtitle() {
    const { t, currentGallection } = this.props;

    const { isPaidFolder, isWatchToAccess } = normalizePremiumFolderData(
      currentGallection?.premiumFolderData
    );

    if (isPaidFolder) {
      return t('mediapopup.editYourGalleryHint.paidFolder');
    }

    if (isWatchToAccess) {
      return t('mediapopup.editYourGalleryHint.watchersOnlyFolder');
    }

    switch (currentGallection?.type) {
      case GallectionType.STASH:
        return t('mediapopup.editYourStashHint');
      case GallectionType.GALLERY:
        return t('mediapopup.editYourGalleryHint');
      case GallectionType.COLLECTION:
      default:
        return t('mediapopup.editYourCollectionHint');
    }
  }

  renderRightZoneButtons() {
    const { currentGallection, currentSubfolder } = this.props;
    return (
      <Flex style={{ gap: 16 }}>
        {canGallectionHaveSubfolders(currentGallection!) &&
          !currentSubfolder && <AddGallectionButton isSecondary />}
        {this.renderPlusDeviationsButton()}
      </Flex>
    );
  }

  renderPlusDeviationsButton(
    buttonView?: SubmitButtonProps['buttonView'],
    plusOnly?: boolean
  ) {
    const { currentGallection, selectionLimit } = this.props;

    if (
      currentGallection?.type === 'stash' ||
      !getCanEditGallection(currentGallection)
    ) {
      return null;
    }

    if (isPremiumGallection(currentGallection!)) {
      return (
        <SubmitDeviationsButton buttonView={buttonView} plusOnly={plusOnly} />
      );
    }

    return (
      <AddDeviationsButton
        plusOnly={plusOnly}
        disabled={!this.canManageThisGallectionContents()}
        limit={selectionLimit}
      />
    );
  }

  canManageThisGallectionContents = () => {
    const { currentGallection, currentSubfolder } = this.props;
    const currentTopLevelGallectionOrSubfolder =
      currentSubfolder || currentGallection;
    const canManageContents =
      currentTopLevelGallectionOrSubfolder &&
      !isSpecialFullyLockedFolder(currentTopLevelGallectionOrSubfolder);
    return canManageContents;
  };

  fetchMoreGallectionDeviationsIfNeeded = () => {
    const { hasMore, isFetching, fetchMoreGallectionDeviations } = this.props;
    if (!hasMore || isFetching) {
      return;
    }
    fetchMoreGallectionDeviations();
  };

  renderContent() {
    const { isInitialized, isFetching, hasMore, deviations } = this.props;
    return (
      <>
        {isInitialized &&
          (!deviations.length && !hasMore ? (
            this.renderEmpty()
          ) : (
            <DeviationGrid
              deviations={deviations}
              deviationRenderer={this.deviationRenderer}
              moveDeviationToPosition={this.props.moveDeviationToPosition}
            />
          ))}
        {(!isInitialized || isFetching) && (
          <LoadingIndicator coverMode={!isInitialized} view="daSymbol" />
        )}
      </>
    );
  }

  renderEmpty() {
    const { t, currentGallection } = this.props;
    const { isPremium, isPaidFolder, isWatchToAccess } =
      normalizePremiumFolderData(currentGallection?.premiumFolderData);
    let headerTransKey = `mediapopup.addYourFirstDeviation.header.${
      currentGallection!.type
    }`;
    let subheaderTransKey = `mediapopup.addYourFirstDeviation.subheader.${
      currentGallection!.type
    }`;
    if (isPremium) {
      if (isPaidFolder) {
        headerTransKey =
          'mediapopup.addYourFirstDeviation.header.premiumFolder.paid';
        subheaderTransKey =
          'mediapopup.addYourFirstDeviation.subheader.premiumFolder.paid';
      }
      if (isWatchToAccess) {
        headerTransKey =
          'mediapopup.addYourFirstDeviation.header.premiumFolder.watchersOnly';
        subheaderTransKey =
          'mediapopup.addYourFirstDeviation.subheader.premiumFolder.watchersOnly';
      }
    }
    return (
      <div
        className={gallectionDeviationManagerStyles['empty-state-container']}
      >
        {this.renderPlusDeviationsButton('b3', true)}
        <div className={gallectionDeviationManagerStyles['empty-state-header']}>
          {t(headerTransKey)}
        </div>
        <div
          className={gallectionDeviationManagerStyles['empty-state-sub-header']}
        >
          <Trans i18nKey={subheaderTransKey}>Add your first deviation</Trans>
        </div>
      </div>
    );
  }

  deviationRenderer = (
    deviation: PapiDeviation,
    index,
    deviations: PapiDeviation[]
  ) => {
    return <ManageableDeviation index={index} deviation={deviation} />;
  };
}

export default enhance(
  GallectionDeviationManager
) as React.ComponentClass<Props>;
